import React, { useEffect, useState } from "react";
import { deleteCustomer, updateCustomer } from "../../services/customerService";
import { Customer } from "../../types/customer";
import { useAuth } from "../../context/AuthContext";
import { isSuperAdmin } from "../../utils/permission";
import { debug } from "../../utils/debugLogger";
import { useToast } from "../../context/ToastContext";
import ConfirmDialog from "../Shared/ConfirmDialog";

interface CustomerUpdateFormProps {
  customer: Partial<Customer>; // Allows flexibility for missing fields
  onCustomerUpdated?: (customer: Customer) => void;
  onCustomerDeleted?: (customerId: string) => void;
}

const CustomerUpdateForm: React.FC<CustomerUpdateFormProps> = ({
  customer,
  onCustomerUpdated,
  onCustomerDeleted,
}) => {
  debug.log("CustomerUpdateForm mounted", customer);
  const [formData, setFormData] = useState({
    customer_id: customer.customer_id || "",
    business_name: customer.business_name || "",
    contact_email: customer.contact_email || "",
    city: customer.city || "",
    state: customer.state || "",
    country: customer.country || "USA",
    manager_email: customer.manager_email || "",
    status: customer.status || "ACTIVE",
    connected_profiles: customer.connected_profiles || [],
    created_at: customer.created_at || "",
  });

  // Sync formData with customer prop when it changes
  useEffect(() => {
    let connectedProfiles = customer.connected_profiles;
    if (typeof connectedProfiles === "string") {
      try {
        connectedProfiles = JSON.parse(connectedProfiles);
      } catch {
        connectedProfiles = [];
      }
    }
    if (!Array.isArray(connectedProfiles)) {
      connectedProfiles = [];
    }
    setFormData({
      customer_id: customer.customer_id || "",
      business_name: customer.business_name || "",
      contact_email: customer.contact_email || "",
      city: customer.city || "",
      state: customer.state || "",
      country: customer.country || "USA",
      manager_email: customer.manager_email || "",
      status: customer.status || "ACTIVE",
      connected_profiles: connectedProfiles,
      created_at: customer.created_at || "",
    });
  }, [customer]);
  const [loading, setLoading] = useState(false);
  const { idToken, userGroups } = useAuth();
  const { showToast } = useToast();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // SGL-357: deleting a Brand currently locks the owner out of creating
  // another one, because the backend gates creation on a counter that only
  // ever increments. Until that fix is in prod, a customer must not be able
  // to reach this button at all. Staff keep it for support work and are
  // warned what it does.
  const canDeleteBrand = isSuperAdmin(userGroups);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteCustomer(formData.customer_id, idToken);
      setDeleteConfirmOpen(false);
      showToast("Business profile deleted", "success");
      if (onCustomerDeleted) onCustomerDeleted(formData.customer_id);
    } catch (error) {
      setDeleteError("Failed to delete business profile. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.customer_id ||
      !formData.business_name ||
      !formData.contact_email
    ) {
      alert("Customer ID, Business Name, and Contact Email are required.");
      return;
    }

    try {
      setLoading(true);
      await updateCustomer(formData, idToken); // Directly pass the formData in snake_case
      alert("Customer updated successfully!");
    } catch (error) {
      debug.error("Error updating customer:", error);
      alert("Failed to update customer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      {/* Business Name */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Business Name
        </label>
        <input
          type="text"
          name="business_name"
          value={formData.business_name}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      {/* Contact Email */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Contact Email
        </label>
        <input
          type="email"
          name="contact_email"
          value={formData.contact_email}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      {/* City */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">City</label>
        <input
          type="text"
          name="city"
          value={formData.city}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      {/* State */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">State</label>
        <input
          type="text"
          name="state"
          value={formData.state}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>
      {/* Country */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">Country</label>
        <input
          type="text"
          name="country"
          value={formData.country}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      {/* Manager Email */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Manager Email
        </label>
        <input
          type="email"
          name="manager_email"
          value={formData.manager_email}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
        />
      </div>

      {/* Status Dropdown */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">Status</label>
        <select
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full p-2 border border-gray-300 rounded"
        >
          {/* The words the backend writes (webhook + common.lifecycle). "INACTIVE" never existed in data. */}
          <option value="ACTIVE">Active</option>
          <option value="ONBOARDING">Onboarding</option>
          <option value="NOT_ACTIVE">Inactive</option>
        </select>
      </div>

      {/* Connected Profiles (readable table format) */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Connected Profiles
        </label>
        {Array.isArray(formData.connected_profiles) &&
        formData.connected_profiles.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-300 bg-gray-50 rounded">
              <thead>
                <tr>
                  <th className="px-2 py-1 border-b">Platform</th>
                  <th className="px-2 py-1 border-b">Profile Name</th>
                  <th className="px-2 py-1 border-b">Profile ID</th>
                  <th className="px-2 py-1 border-b">Category</th>
                  <th className="px-2 py-1 border-b">Type</th>
                </tr>
              </thead>
              <tbody>
                {formData.connected_profiles.flatMap((profile, idx) => {
                  if (Array.isArray(profile.details)) {
                    return profile.details.map((detail: any, dIdx: number) => (
                      <tr key={`${idx}-${dIdx}`}>
                        <td className="px-2 py-1 border-b">
                          {profile.platform || "-"}
                        </td>
                        <td className="px-2 py-1 border-b">
                          {detail.metadata?.name || "-"}
                        </td>
                        <td className="px-2 py-1 border-b">
                          {detail.id || "-"}
                        </td>
                        <td className="px-2 py-1 border-b">
                          {detail.metadata?.category || "-"}
                        </td>
                        <td className="px-2 py-1 border-b">
                          {detail.metadata?.type || "-"}
                        </td>
                      </tr>
                    ));
                  } else {
                    // fallback for unexpected structure
                    return (
                      <tr key={idx}>
                        <td className="px-2 py-1 border-b">
                          {profile.platform || "-"}
                        </td>
                        <td className="px-2 py-1 border-b">-</td>
                        <td className="px-2 py-1 border-b">-</td>
                        <td className="px-2 py-1 border-b">-</td>
                        <td className="px-2 py-1 border-b">-</td>
                      </tr>
                    );
                  }
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <pre className="w-full p-2 border border-gray-300 rounded bg-gray-50 overflow-x-auto text-xs">
            {JSON.stringify(formData.connected_profiles, null, 2)}
          </pre>
        )}
      </div>

      {/* Created At (read-only) */}
      <div>
        <label className="block text-gray-700 font-medium mb-1">
          Created At
        </label>
        <input
          type="text"
          value={formData.created_at}
          readOnly
          className="w-full p-2 border border-gray-200 rounded bg-surface-ui"
        />
      </div>
      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg"
      >
        {loading ? "Updating..." : "Update Customer"}
      </button>
      {canDeleteBrand && (
        <button
          type="button"
          onClick={() => {
            setDeleteError(null);
            setDeleteConfirmOpen(true);
          }}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg ml-2"
        >
          Delete Customer
        </button>
      )}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title={
          formData.business_name
            ? `Delete ${formData.business_name}?`
            : "Delete this business profile?"
        }
        message="This removes the business profile and its brand. The owner can't create a replacement brand until a fix ships, so only continue if you're ready to fix their record by hand."
        confirmLabel="Delete brand"
        destructive
        isConfirming={isDeleting}
        errorMessage={deleteError}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </form>
  );
};

export default CustomerUpdateForm;
